import { useCallback, useEffect, useState } from 'react';
import { appeler, EchecApi } from '../api.js';

/**
 * CHARGER UNE RESSOURCE — le mecanisme commun a tous les ecrans.
 *
 * ╔═══════════════════════════════════════════════════════════════════════╗
 * ║ TROIS ETATS, ET UN SEUL A L'ECRAN.                                    ║
 * ╚═══════════════════════════════════════════════════════════════════════╝
 *
 *   chargement   on ne sait pas encore
 *   pret         les donnees sont la
 *   echec        l'API a refuse ou n'a pas repondu
 *
 * VIDE N'EST PAS UN ETAT DE CE HOOK. Une liste vide est une INTERPRETATION
 * de la donnee chargee, faite par l'ecran : phase pret, zero ligne. C'est ainsi
 * qu'Automatisations.tsx la rend, et Versions.tsx fera de meme. La modeliser
 * ici obligerait ce hook a connaitre la forme de T pour savoir la compter.
 *
 * Le troisieme porte le CODE de l'API, pas seulement un message :
 * un 403 se traite autrement qu'un 500.
 *
 * ── L'ECRAN NE DECIDE PAS S'IL A LE DROIT ───────────────────────────────
 *
 * Il appelle, et l'API tranche. Un 403 s'affiche comme un refus explicite,
 * jamais comme une page vide : l'utilisateur doit savoir que la porte existe et
 * qu'elle lui est fermee, pas croire que la rubrique est en panne.
 */

export type Chargement<T> =
  | { readonly phase: 'chargement' }
  | { readonly phase: 'pret'; readonly donnees: T }
  | { readonly phase: 'echec'; readonly code: string; readonly message: string };

export interface Ressource<T> {
  readonly etat: Chargement<T>;
  readonly recharger: () => void;
}

export function useChargement<T>(chemin: string): Ressource<T> {
  const [etat, setEtat] = useState<Chargement<T>>({ phase: 'chargement' });
  const [tour, setTour] = useState(0);

  useEffect(() => {
    // ANNULE SI LE COMPOSANT DISPARAIT. Sans cela, une reponse tardive
    // ecraserait l'etat d'un ecran que l'utilisateur a deja quitte -- et
    // React signalerait une mise a jour sur un composant demonte.
    let vivant = true;
    setEtat({ phase: 'chargement' });
    void appeler<T>('GET', chemin)
      .then((donnees) => { if (vivant) setEtat({ phase: 'pret', donnees }); })
      .catch((e: unknown) => {
        if (!vivant) return;
        const d = e instanceof EchecApi ? e.detail : undefined;
        setEtat({ phase: 'echec',
          code: d?.code ?? 'injoignable',
          message: d?.message ?? 'Le service est injoignable.' });
      });
    return () => { vivant = false; };
  }, [chemin, tour]);

  const recharger = useCallback(() => { setTour((t) => t + 1); }, []);
  return { etat, recharger };
}

/**
 * CHARGER UNE LISTE PAGINEE — le curseur en plus.
 *
 * ── LE CURSEUR EST OPAQUE, ET LE RESTE ──────────────────────────────────
 *
 * L'API le rend, l'interface le renvoie tel quel. Le decoder ici -- il porte
 * une date et un identifiant en base64url -- creerait une dependance a sa
 * forme, que l'API a le droit de changer sans prevenir.
 *
 * ── « CHARGER LA SUITE », PAS UN DEFILEMENT INFINI ──────────────────────
 *
 * Un defilement infini rend le bas de page inatteignable, casse le retour
 * arriere du navigateur et empeche d'atteindre ce qui suit la liste. Une liste
 * d'automatisations n'est pas un fil d'actualite.
 *
 * ── DETTE 79 [29-I] : PAS DE GARDE DE DEMONTAGE ICI ────────────────────
 *
 * `useChargement`, juste au-dessus, annule sa reponse tardive par un drapeau
 * `vivant`. `useListePaginee` ne le fait PAS : une reponse qui revient apres
 * le demontage appelle encore `setEtat`.
 *
 * NON CORRIGEE EN 29-I, ET DELIBEREMENT : aucune panne n a ete mesuree, et
 * toucher ce hook aurait remis en jeu ses consommateurs existants sans motif.
 * Mais l ecart est REQUALIFIE : la dependance passe de DEUX consommateurs a
 * QUATRE (Automatisations, Projets, Plateforme, ClientPlateforme), et les
 * deux derniers sont les PREMIERS a consommer reellement `chargerSuite` --
 * donc a declencher une seconde requete qu un demontage peut devancer.
 *
 * CONDITION DE LEVEE : reprendre le drapeau `vivant` de `useChargement`,
 * DANS `charger` et dans `chargerSuite`, et le prouver par une execution --
 * une preuve qui lirait le fichier garderait la forme, pas l effet.
 */
export interface Page<L> {
  readonly lignes: readonly L[];
  readonly curseurSuivant: string | null;
  readonly chargeSuite: boolean;
}

export interface ListePaginee<L> {
  readonly etat: Chargement<Page<L>>;
  readonly chargerSuite: () => void;
  readonly recharger: () => void;
}

export function useListePaginee<R, L>(
  chemin: string,
  extraire: (r: R) => { lignes: readonly L[]; curseur: string | null },
): ListePaginee<L> {
  const [etat, setEtat] = useState<Chargement<Page<L>>>({ phase: 'chargement' });
  const [tour, setTour] = useState(0);

  const charger = useCallback((curseur: string | null, accumuler: boolean) => {
    const separateur = chemin.includes('?') ? '&' : '?';
    const url = curseur === null ? chemin
      : chemin + separateur + 'curseur=' + encodeURIComponent(curseur);
    void appeler<R>('GET', url)
      .then((r) => {
        const { lignes, curseur: suivant } = extraire(r);
        setEtat((precedent) => {
          // ACCUMULER, jamais remplacer : « charger la suite » ajoute a ce qui
          // est deja lu. Remplacer ferait disparaitre la premiere page sous
          // les yeux de qui a clique.
          const deja = accumuler && precedent.phase === 'pret'
            ? precedent.donnees.lignes : [];
          return { phase: 'pret',
            donnees: { lignes: [...deja, ...lignes], curseurSuivant: suivant,
                       chargeSuite: false } };
        });
      })
      .catch((e: unknown) => {
        const d = e instanceof EchecApi ? e.detail : undefined;
        setEtat({ phase: 'echec',
          code: d?.code ?? 'injoignable',
          message: d?.message ?? 'Le service est injoignable.' });
      });
  }, [chemin, extraire]);

  useEffect(() => {
    setEtat({ phase: 'chargement' });
    charger(null, false);
  }, [charger, tour]);

  const chargerSuite = useCallback(() => {
    setEtat((p) => {
      if (p.phase !== 'pret' || p.donnees.curseurSuivant === null) return p;
      charger(p.donnees.curseurSuivant, true);
      // L'ETAT DIT QU'ON CHARGE : sans cela, un second clic partirait avant
      // le retour du premier et les deux pages s'ajouteraient deux fois.
      return { phase: 'pret', donnees: { ...p.donnees, chargeSuite: true } };
    });
  }, [charger]);

  const recharger = useCallback(() => { setTour((t) => t + 1); }, []);
  return { etat, chargerSuite, recharger };
}
